import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import { 
  CalendarIcon, 
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  TruckIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  StopIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  PhoneIcon,
  ChatBubbleBottomCenterTextIcon,
  StarIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

function RidesManagement() {
  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [selectedRide, setSelectedRide] = useState(null);
  const [showRideModal, setShowRideModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    fetchRides();
  }, []);

  useEffect(() => {
    filterRides();
  }, [rides, searchTerm, selectedStatus, selectedDateRange]);

  const fetchRides = async () => {
    try {
      setLoading(true);
      const [ridesResponse, bookingsResponse] = await Promise.all([
        axiosInstance.get(API_URLS.admin.rides),
        axiosInstance.get(API_URLS.admin.bookings),
      ]);
      if (!Array.isArray(ridesResponse.data) || !Array.isArray(bookingsResponse.data)) {
        throw new Error('The server returned invalid rides or bookings data.');
      }
      setRides(ridesResponse.data);
      setBookings(bookingsResponse.data);
    } catch (err) {
      console.error('Error fetching rides:', err);
      setError('Failed to fetch rides');
    } finally {
      setLoading(false);
    }
  };

  const filterRides = () => {
    let filtered = rides.filter(ride => {
      const matchesSearch = 
        ride.pickupLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ride.dropoffLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ride.driver?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ride.driver?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || ride.status === selectedStatus;
      
      let matchesDateRange = true;
      if (selectedDateRange !== 'all') {
        const rideDate = new Date(ride.date);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        switch (selectedDateRange) {
          case 'upcoming':
            matchesDateRange = rideDate >= today;
            break;
          case 'past':
            matchesDateRange = rideDate < today;
            break;
          case 'today':
            matchesDateRange = rideDate.toDateString() === today.toDateString();
            break;
          default:
            matchesDateRange = true;
        }
      }

      return matchesSearch && matchesStatus && matchesDateRange;
    });

    setFilteredRides(filtered);
  };

  const rideParticipants = selectedRide
    ? bookings
      .filter((booking) => (
        String(booking.rideId || booking.Ride?.id || booking.ride?.id) === String(selectedRide.id) &&
        String(booking.status || '').toLowerCase() !== 'cancelled' &&
        booking.rider
      ))
      .reduce((participants, booking) => {
        const riderId = booking.rider.id || booking.riderId;
        if (!participants.some((rider) => (
          riderId && String(rider.id || rider.riderId) === String(riderId)
        ))) {
          participants.push({ ...booking.rider, riderId });
        }
        return participants;
      }, [])
    : [];

  const handleRideAction = async (rideId, action) => {
    try {
      switch (action) {
        case 'view':
          const ride = rides.find(r => r.id === rideId);
          setSelectedRide(ride);
          setShowRideModal(true);
          break;
        case 'edit':
          console.log('Edit ride:', rideId);
          break;
        case 'cancel':
          if (window.confirm('Are you sure you want to cancel this ride?')) {
            // TODO: Implement cancel ride API call
            console.log('Cancel ride:', rideId);
          }
          break;
        case 'force-end':
          if (window.confirm('Are you sure you want to force end this ride?')) {
            // TODO: Implement force end API call
            console.log('Force end ride:', rideId);
          }
          break;
        case 'reassign':
          // TODO: Implement reassign driver functionality
          console.log('Reassign ride:', rideId);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error performing ride action:', err);
      setError('Failed to perform action');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getServiceBadge = (serviceSession) => {
    if (!serviceSession) return null;
    
    return (
      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
        {serviceSession} Service
      </span>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rides Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor and manage all rides, schedules, and transportation
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {[
          {
            title: 'Total Rides',
            value: rides.length,
            icon: CalendarIcon,
            color: 'bg-blue-500'
          },
          {
            title: 'Upcoming',
            value: rides.filter(r => r.status === 'scheduled' && new Date(r.date) >= new Date()).length,
            icon: ClockIcon,
            color: 'bg-green-500'
          },
          {
            title: 'Completed',
            value: rides.filter(r => r.status === 'completed').length,
            icon: CalendarIcon,
            color: 'bg-purple-500'
          },
          {
            title: 'Cancelled',
            value: rides.filter(r => r.status === 'cancelled').length,
            icon: StopIcon,
            color: 'bg-red-500'
          }
        ].map((stat, index) => (
          <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`${stat.color} rounded-md p-3`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.title}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
            {/* Search */}
            <div className="sm:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Search Rides
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by location, driver name..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div>
              <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700">
                Date Range
              </label>
              <select
                id="dateRange"
                value={selectedDateRange}
                onChange={(e) => setSelectedDateRange(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
              >
                <option value="all">All Dates</option>
                <option value="upcoming">Upcoming</option>
                <option value="today">Today</option>
                <option value="past">Past</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Rides Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Rides ({filteredRides.length})
          </h3>
        </div>
        
        {filteredRides.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No rides found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Route & Driver
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capacity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRides.map((ride) => (
                  <tr key={ride.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <MapPinIcon className="h-6 w-6 text-indigo-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="space-y-1 whitespace-nowrap text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Pickup:</span>{' '}
                              <span className="text-gray-900">{ride.pickupLocation || 'Not provided'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Destination:</span>{' '}
                              <span className="text-gray-900">{ride.dropoffLocation || 'Not provided'}</span>
                            </div>
                            <div className="text-gray-500">
                              Driver: {ride.driver?.firstName} {ride.driver?.lastName}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(ride.date).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">{ride.time}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {ride.totalSeats - ride.availableSeats}/{ride.totalSeats}
                      </div>
                      <div className="text-sm text-gray-500">
                        {ride.availableSeats} available
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(ride.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getServiceBadge(ride.serviceSession)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleRideAction(ride.id, 'view')}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRideAction(ride.id, 'edit')}
                          className="text-green-600 hover:text-green-900"
                          title="Edit Ride"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {ride.status === 'scheduled' && (
                          <button
                            onClick={() => handleRideAction(ride.id, 'cancel')}
                            className="text-red-600 hover:text-red-900"
                            title="Cancel Ride"
                          >
                            <StopIcon className="h-4 w-4" />
                          </button>
                        )}
                        {ride.status === 'in-progress' && (
                          <button
                            onClick={() => handleRideAction(ride.id, 'force-end')}
                            className="text-orange-600 hover:text-orange-900"
                            title="Force End"
                          >
                            <StopIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ride Details Modal */}
      {showRideModal && selectedRide && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRideModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Ride Details - #{selectedRide.id}
                    </h3>
                    
                    <div className="space-y-6">
                      {/* Route Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Route Information</h4>
                        <div className="grid grid-cols-1 gap-4">
                          <div className="flex items-center">
                            <MapPinIcon className="h-5 w-5 text-green-500 mr-2" />
                            <div>
                              <span className="text-sm font-medium">Pickup:</span>
                              <span className="text-sm text-gray-900 ml-2">{selectedRide.pickupLocation}</span>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <MapPinIcon className="h-5 w-5 text-red-500 mr-2" />
                            <div>
                              <span className="text-sm font-medium">Destination:</span>
                              <span className="text-sm text-gray-900 ml-2">{selectedRide.dropoffLocation}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Schedule Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Schedule</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center">
                            <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {new Date(selectedRide.date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{selectedRide.time}</span>
                          </div>
                        </div>
                      </div>

                      {/* Driver & Vehicle Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Driver & Vehicle</h4>
                        <div className="grid grid-cols-1 gap-4">
                          <div className="flex items-center">
                            <TruckIcon className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {selectedRide.driver?.firstName} {selectedRide.driver?.lastName}
                            </span>
                          </div>
                          {selectedRide.vehicle && (
                            <div className="flex items-center">
                              <span className="text-sm text-gray-600">
                                {selectedRide.vehicle.make} {selectedRide.vehicle.model} - {selectedRide.vehicle.licensePlate}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Capacity & Status */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Capacity & Status</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <span className="text-sm font-medium text-gray-700">Total Seats:</span>
                            <span className="text-sm text-gray-900 ml-2">{selectedRide.totalSeats}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Available:</span>
                            <span className="text-sm text-gray-900 ml-2">{selectedRide.availableSeats}</span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-700">Status:</span>
                            <span className="ml-2">{getStatusBadge(selectedRide.status)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Admin Actions */}
                      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Admin Actions</h4>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            disabled
                            title="Coming soon"
                            onClick={() => handleRideAction(selectedRide.id, 'reassign')}
                            className="inline-flex cursor-not-allowed items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gray-500 bg-gray-100 opacity-75"
                          >
                            <ArrowPathIcon className="h-4 w-4 mr-1" />
                            Reassign Driver
                            <span className="ml-1 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                              Coming soon
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowParticipantModal(true)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                          >
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            Contact Participants
                          </button>
                          <button
                            type="button"
                            disabled
                            title="Coming soon"
                            className="inline-flex cursor-not-allowed items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-gray-500 bg-gray-100 opacity-75"
                          >
                            <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mr-1" />
                            View Chat Logs
                            <span className="ml-1 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                              Coming soon
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowRideModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowRideModal(false);
                    handleRideAction(selectedRide.id, 'edit');
                  }}
                >
                  Edit Ride
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showParticipantModal && selectedRide && (
        <div className="fixed inset-0 z-[60] overflow-y-auto" role="presentation">
          <div className="flex min-h-screen items-center justify-center p-4">
            <button
              type="button"
              className="fixed inset-0 bg-gray-900/50"
              aria-label="Close participant contacts"
              onClick={() => setShowParticipantModal(false)}
            />
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="participant-contacts-title"
              className="relative z-10 w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 id="participant-contacts-title" className="text-lg font-semibold text-gray-900">
                    Ride participants
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Ride #{selectedRide.id} · {selectedRide.pickupLocation} to {selectedRide.dropoffLocation}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowParticipantModal(false)}
                  className="rounded-md px-2 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  Close
                </button>
              </div>

              <div className="mt-6 space-y-5">
                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Driver</h3>
                  {selectedRide.driver ? (
                    <div className="mt-2 rounded-md bg-gray-50 p-4">
                      <p className="font-medium text-gray-900">
                        {`${selectedRide.driver.firstName || ''} ${selectedRide.driver.lastName || ''}`.trim() || 'Name not provided'}
                      </p>
                      {selectedRide.driver.phone ? (
                        <a className="mt-2 flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900" href={`tel:${selectedRide.driver.phone}`}>
                          <PhoneIcon className="h-4 w-4" />{selectedRide.driver.phone}
                        </a>
                      ) : (
                        <p className="mt-2 text-sm text-gray-500">Phone not provided</p>
                      )}
                      {selectedRide.driver.email ? (
                        <a className="mt-2 flex items-center gap-2 break-all text-sm text-indigo-700 hover:text-indigo-900" href={`mailto:${selectedRide.driver.email}`}>
                          <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />{selectedRide.driver.email}
                        </a>
                      ) : (
                        <p className="mt-2 text-sm text-gray-500">Email not provided</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">No driver is listed for this ride.</p>
                  )}
                </section>

                <section>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                    Riders ({rideParticipants.length})
                  </h3>
                  {rideParticipants.length > 0 ? (
                    <ul className="mt-2 max-h-64 space-y-2 overflow-y-auto">
                      {rideParticipants.map((rider, index) => (
                        <li key={rider.id || rider.riderId || `${rider.email}-${index}`} className="rounded-md bg-gray-50 p-4">
                          <p className="font-medium text-gray-900">
                            {`${rider.firstName || ''} ${rider.lastName || ''}`.trim() || 'Name not provided'}
                          </p>
                          {rider.phone ? (
                            <a className="mt-2 flex items-center gap-2 text-sm text-indigo-700 hover:text-indigo-900" href={`tel:${rider.phone}`}>
                              <PhoneIcon className="h-4 w-4" />{rider.phone}
                            </a>
                          ) : (
                            <p className="mt-2 text-sm text-gray-500">Phone not provided</p>
                          )}
                          {rider.email ? (
                            <a className="mt-2 flex items-center gap-2 break-all text-sm text-indigo-700 hover:text-indigo-900" href={`mailto:${rider.email}`}>
                              <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />{rider.email}
                            </a>
                          ) : (
                            <p className="mt-2 text-sm text-gray-500">Email not provided</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">No non-cancelled rider bookings are linked to this ride.</p>
                  )}
                </section>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default RidesManagement; 